import { Skeleton } from '@/components/ui/skeleton'
import {
	Card,
	CardHeader,
	CardTitle,
	CardContent,
	CardFooter,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Filter, Star } from 'lucide-react'

// Re-using the dashboard skeleton's enhanced styling approach
const SkeletonBase = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<Skeleton
		className={cn('bg-slate-200 dark:bg-slate-800', className)}
		{...props}
	/>
)

const AnalyticsCardSkeleton = () => (
	<Card>
		<CardHeader>
			<SkeletonBase className="h-4 w-32 rounded" />
		</CardHeader>
		<CardContent>
			<SkeletonBase className="h-8 w-20 mb-2 rounded" />
			<SkeletonBase className="h-4 w-40 rounded" />
		</CardContent>
	</Card>
)

const RatingDistributionSkeleton = () => (
	<Card>
		<CardHeader>
			<SkeletonBase className="h-4 w-32 rounded" />
		</CardHeader>
		<CardContent className="space-y-3">
			{Array.from({ length: 5 }).map((_, i) => (
				<div key={i} className="flex items-center gap-3">
					<SkeletonBase className="h-4 w-8 rounded" />
					<SkeletonBase className="h-2 flex-1 rounded-full" />
					<SkeletonBase className="h-4 w-8 rounded" />
				</div>
			))}
		</CardContent>
	</Card>
)

const RatingTrendSkeleton = () => (
	<Card className="lg:col-span-1">
		<CardHeader>
			<SkeletonBase className="h-4 w-24 rounded" />
		</CardHeader>
		<CardContent>
			<SkeletonBase className="h-[158px] w-full rounded-md" />
		</CardContent>
	</Card>
)

const ReviewCardSkeleton = () => (
	<Card className="flex flex-col">
		<CardHeader>
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-center gap-3 flex-1 min-w-0">
					<SkeletonBase className="h-10 w-10 rounded-full" />
					<div className="min-w-0 space-y-1.5">
						<SkeletonBase className="h-4 w-32 rounded" />
						<SkeletonBase className="h-3 w-24 rounded" />
					</div>
				</div>
				<div className="flex flex-col items-end flex-shrink-0 space-y-1.5">
					<SkeletonBase className="h-4 w-20 rounded" />
					<SkeletonBase className="h-3 w-16 rounded" />
				</div>
			</div>
		</CardHeader>
		<CardContent className="flex-grow space-y-1.5">
			<SkeletonBase className="h-4 w-full rounded" />
			<SkeletonBase className="h-4 w-full rounded" />
			<SkeletonBase className="h-4 w-3/4 rounded" />
		</CardContent>
		<CardFooter className="border-t p-4 flex items-center justify-between">
			<SkeletonBase className="h-5 w-20 rounded-full" />
			<div className="flex gap-2">
				<SkeletonBase className="h-8 w-8 rounded-md" />
				<SkeletonBase className="h-8 w-8 rounded-md" />
				<SkeletonBase className="h-8 w-8 rounded-md" />
			</div>
		</CardFooter>
	</Card>
)

export const ReviewsPageSkeleton = () => {
	return (
		<div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8">
			{/* Header Skeleton */}
			<div className="mb-6">
				<SkeletonBase className="h-8 w-1/4 rounded mb-2" />
				<SkeletonBase className="h-4 w-1/2 rounded" />
			</div>

			{/* Analytics Skeleton */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				<AnalyticsCardSkeleton />
				<RatingDistributionSkeleton />
				<RatingTrendSkeleton />
			</div>

			{/* Filters Skeleton */}
			<Card className="mb-8">
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-lg font-medium">
						<Filter className="h-5 w-5 text-muted-foreground" />
						<SkeletonBase className="h-6 w-20 rounded" />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
						<SkeletonBase className="h-10 lg:col-span-2 rounded-md" />{' '}
						{/* Search */}
						<SkeletonBase className="h-10 rounded-md" /> {/* Status */}
						<SkeletonBase className="h-10 rounded-md" /> {/* Timeframe */}
						<SkeletonBase className="h-10 w-24 justify-self-end rounded-md" />{' '}
						{/* Reset */}
					</div>
				</CardContent>
			</Card>

			{/* Reviews List Skeleton */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<ReviewCardSkeleton />
				<ReviewCardSkeleton />
				<ReviewCardSkeleton />
				<ReviewCardSkeleton />
			</div>
		</div>
	)
}
